import React, { useState } from "react";
import {
  UseFormRegister,
  FieldErrors,
  UseFormSetValue,
  UseFormClearErrors,
} from "react-hook-form";
import Input from "../../../components/Input";
import SelectInput from "../../../components/Select";
import TextareaField from "../../../components/common/TextareaField";
import {
  OrderFormData,
  PAYMENT_TYPE_OPTIONS,
  ORDER_SHIPMENT_TYPE_OPTIONS,
} from "../../../types/types";
import { useServiceAllListData } from "../../../apis/orderApis";
import { IconBox, IconPackage } from "@tabler/icons-react";
import NewinputField from "../../../components/NewInput";
import NewSelectInput from "../../../components/NewSelectInput";
import { useMerchantAllListData } from "../../../apis/merchantApis";
import { useLanguage } from "../../../context/LanguageContext";

interface ShipmentSectionProps {
  clients: any[];
  register: UseFormRegister<OrderFormData>;
  open: boolean;
  onClose: () => void;
  errors: FieldErrors<OrderFormData>;
  setValue: UseFormSetValue<OrderFormData>;
  watch: any;
  clearErrors: UseFormClearErrors<OrderFormData>;
  mode: "add" | "view";
  setMode: (mode: "add" | "view") => void;
  isSidebar?: boolean;
  isSubmitted?: boolean;
  disableWeight?: boolean;
  useFMOrder?: boolean;
  setUseFMOrder?: (value: boolean) => void;
}

const SectionHeader = ({
  icon: Icon,
  title,
  className = "border-b border-[#FBD037]/30 pb-2 mb-3",
}: {
  icon: any;
  title: string;
  className?: string;
}) => {
  const { t } = useLanguage();
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <div className="p-1.5 rounded-lg bg-[#FBD037]/20">
        <Icon size={16} className="text-[#212121]" stroke={1.5} />
      </div>
      <p className="text-[#212121]/80 uppercase text-[11px] font-bold tracking-wide">
        {t(title)}
      </p>
    </div>
  );
};

const ShipmentSection: React.FC<ShipmentSectionProps> = ({
  register,
  clients,
  errors,
  setValue,
  watch,
  useFMOrder: propUseFMOrder, // renamed to avoid conflict
  setUseFMOrder: propSetUseFMOrder,
  clearErrors,
  isSidebar = false,
  isSubmitted = false,
  disableWeight = false,
}) => {
  const { t } = useLanguage();
  const { data } = useServiceAllListData();

  const useFMOrder = propUseFMOrder ?? false;
  const setUseFMOrder = propSetUseFMOrder ?? (() => {});

  const selectedClientId = watch("clientId");
  const selectedClient = clients?.find((c: any) => c?.id === selectedClientId);
  const isNippcoMerchant = selectedClient?.isNippco === true;

  const getServiceOptions = () => {
    let options = data?.map((s: any) => ({
      id: s?.id,
      value: s?.name,
      text: s?.name,
    })) || [];
    if (isNippcoMerchant) {
      const hasSameDay = options.some((opt: any) => opt.value === "SAME_DAY");
      if (!hasSameDay) {
        options = [...options, { id: "SAME_DAY", value: "SAME_DAY", text: t("Same Day") }];
      }
    }
    return options;
  };

  if (isSidebar) {
    return (
      <div className="relative focus-within:z-40 group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3">
        <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
        {/* Section header */}

        <div className="flex justify-between items-center border-b border-[#FBD037]/30 pb-2 mb-3">
          <SectionHeader
            icon={IconPackage}
            title="Shipment Details"
            className="w-full"
          />
          {clients?.find((c: any) => c.id === watch("clientId"))?.access ===
            "BOTH" && (
            <button
              type="button"
              onClick={() => setUseFMOrder(!useFMOrder)}
              className={`flex items-center gap-1.5 px-2.5 py-0.5 rounded-full border text-xs font-bold transition-all ${
                useFMOrder
                  ? "bg-[#FBD037] border-[#FBD037] text-[#212121]"
                  : "bg-white border-gray-300 text-gray-500 hover:border-[#FBD037]"
              }`}
            >
              <span
                className={`w-1.5 h-1.5 rounded-full ${useFMOrder ? "bg-[#212121]" : "bg-gray-400"}`}
              />
              FM
            </button>
          )}
        </div>

        <div className="grid grid-cols-6 gap-4 relative z-10">
          {/* Ref */}
          <div className="col-span-3">
            <NewinputField
              name="shipment.referenceNumber"
              type="text"
              title={t("Ref")}
              register={register}
              error={errors.shipment?.referenceNumber}
              validation={{
                required: t("Required"),
                minLength: { value: 4, message: t("Min 4") },
                maxLength: { value: 16, message: t("Max 16") },
              }}
              className="space-y-1"
            />
          </div>

          <div className="col-span-3">
            <NewSelectInput
              name="shipment.serviceType"
              label={t("Service")}
              placeholder={t("Select")}
              className="w-full"
              selectOptions={getServiceOptions()}
              clearErrors={clearErrors}
              register={register}
              validation={{ required: t("Required") }}
              error={errors.shipment?.serviceType}
              value={watch("shipment.serviceType")}
              setValue={(val: string) => setValue("shipment.serviceType", val)}
            />
          </div>

          {/* Cost */}
          <div className="col-span-2">
            <NewinputField
              name="shipment.shipmentCost"
              type="text"
              title={t("Cost")}
              register={register}
              error={errors.shipment?.shipmentCost}
              validation={{
                pattern: { value: /^\d+(\.\d{1,2})?$/, message: t("e.g. 10.50") },
                validate: (v) => !v || parseFloat(v) > 0 || t("Must be > 0"),
              }}
              className="space-y-1"
            />
          </div>

          {/* Pieces */}
          <div className="col-span-2">
            <NewinputField
              name="shipment.numberOfPieces"
              type="number"
              title={t("Pieces")}
              register={register}
              error={isSubmitted ? errors.shipment?.numberOfPieces : undefined}
              validation={{
                required: t("Required"),
                min: { value: 1, message: t("Min 1") },
                validate: (v) => Number.isInteger(Number(v)) || t("Whole number"),
              }}
              className="space-y-1"
            />
          </div>

          {/* Weight */}
          <div className="col-span-2">
            <NewinputField
              name="shipment.totalWeight"
              type="number"
              title={t("Weight")}
              register={register}
              error={errors.shipment?.totalWeight}
              // disabled={true}
              disabled={disableWeight}
              className="space-y-1"
            />
          </div>

          {/* Service */}

          {/* Type */}
          <div className="col-span-2">
            <NewSelectInput
              name="shipment.shipmentType"
              label={t("Type")}
              placeholder={t("Select")}
              className="w-full"
              selectOptions={ORDER_SHIPMENT_TYPE_OPTIONS.map((opt) => ({
                ...opt,
                text: t(opt.text),
              }))}
              clearErrors={clearErrors}
              register={register}
              validation={{ required: t("Required") }}
              error={errors.shipment?.shipmentType}
              value={watch("shipment.shipmentType")}
              setValue={(val: string) => setValue("shipment.shipmentType", val)}
            />
          </div>

          {/* Payment – conditionally take 1 or 2 columns */}
          <div
            className={
              watch("shipment.paymentType") === "COD"
                ? "col-span-2"
                : "col-span-4"
            }
          >
            <NewSelectInput
              name="shipment.paymentType"
              label={t("Payment")}
              placeholder={t("Select")}
              className="w-full"
              selectOptions={PAYMENT_TYPE_OPTIONS.map((opt) => ({
                ...opt,
                text: t(opt.text),
              }))}
              clearErrors={clearErrors}
              register={register}
              value={watch("shipment.paymentType")}
              error={errors.shipment?.paymentType}
              setValue={(val: string) => {
                setValue("shipment.paymentType", val);
                if (val !== "COD") setValue("shipment.codAmount", "");
              }}
              validation={{ required: t("Required") }}
            />
          </div>

          {/* COD Amount – only rendered when visible, always 1 column */}
          {watch("shipment.paymentType") === "COD" && (
            <div className="col-span-2">
              <NewinputField
                name="shipment.codAmount"
                type="number"
                title={t("COD Amt")}
                register={register}
                error={errors.shipment?.codAmount}
                validation={{ required: t("Required") }}
                className="space-y-1"
              />
            </div>
          )}

          {/* Description – full width */}
          <div className="col-span-6">
            <NewinputField
              name="shipment.description"
              type="text"
              title={t("Description")}
              register={register}
              error={errors.shipment?.description}
              validation={{}}
              className="space-y-1 col-span-4"
            />
          </div>
        </div>
      </div>
    );
  }

  // Non-sidebar original grid layout
  return (
    <div className="grid grid-cols-4 gap-2 bg-[#FFFFFF] p-4 rounded-xl shadow-md h-fit">
      <p className="text-black font-medium text-base uppercase col-span-4 pb-2">
        {t("Shipment Details")}
      </p>
      <Input
        name="shipment.referenceNumber"
        type="text"
        title={t("Reference")}
        register={register}
        error={errors.shipment?.referenceNumber}
        validation={{
          required: t("Reference is required"),
          minLength: { value: 4, message: t("Min 4 chars") },
          maxLength: { value: 16, message: t("Max 16 chars") },
          validate: (v) =>
            (v.trim().length >= 4 && v.trim().length <= 16) ||
            t("4–16 characters"),
        }}
        className=""
      />
      <Input
        name="shipment.shipmentCost"
        type="text"
        title={t("Shipment Cost")}
        register={register}
        error={errors.shipment?.shipmentCost}
        validation={{
          pattern: {
            value: /^\d+(\.\d{1,2})?$/,
            message: t("Valid positive number"),
          },
          validate: (v) => !v || parseFloat(v) > 0 || t("Must be > 0"),
        }}
        className=""
      />
      <SelectInput
        name="shipment.paymentType"
        label={t("Payment Type")}
        className=""
        placeholder={t("Select Payment Type")}
        selectOptions={PAYMENT_TYPE_OPTIONS.map((opt) => ({
          ...opt,
          text: t(opt.text),
        }))}
        clearErrors={clearErrors}
        register={register}
        value={watch("shipment.paymentType")}
        error={errors.shipment?.paymentType}
        setValue={(val: string) => {
          setValue("shipment.paymentType", val);
          if (val !== "COD") setValue("shipment.codAmount", "");
        }}
        validation={{ required: t("Payment Type is required") }}
      />
      {watch("shipment.paymentType") === "COD" && (
        <Input
          name="shipment.codAmount"
          type="number"
          title={t("COD Amount")}
          register={register}
          error={errors.shipment?.codAmount}
          validation={{ required: t("COD amount is required") }}
          className=""
        />
      )}
      <Input
        name="shipment.numberOfPieces"
        type="number"
        title={t("No of Pieces")}
        register={register}
        error={isSubmitted ? errors.shipment?.numberOfPieces : undefined}
        validation={{
          required: t("Required"),
          min: { value: 1, message: t("Min 1") },
          validate: (v) => Number.isInteger(Number(v)) || t("Whole number"),
        }}
        className=""
      />
      <Input
        name="shipment.totalWeight"
        type="number"
        title={t("Total Weight (kg)")}
        register={register}
        error={errors.shipment?.totalWeight}
        disabled={disableWeight}
        className=""
      />
      <SelectInput
        name="shipment.serviceType"
        label={t("Service")}
        placeholder={t("Select Service")}
        selectOptions={getServiceOptions()}
        clearErrors={clearErrors}
        register={register}
        validation={{ required: t("Service is required") }}
        error={errors.shipment?.serviceType}
        value={watch("shipment.serviceType")}
        setValue={(val: string) => setValue("shipment.serviceType", val)}
      />
      <SelectInput
        name="shipment.shipmentType"
        label={t("Shipment Type")}
        className=""
        placeholder={t("Select Shipment Type")}
        selectOptions={ORDER_SHIPMENT_TYPE_OPTIONS.map((opt) => ({
          ...opt,
          text: t(opt.text),
        }))}
        clearErrors={clearErrors}
        register={register}
        validation={{ required: t("Shipment type is required") }}
        error={errors.shipment?.shipmentType}
        value={watch("shipment.shipmentType")}
        setValue={(val: string) => setValue("shipment.shipmentType", val)}
      />
      <TextareaField
        rows={2}
        className="col-span-4"
        label={t("Description")}
        register={register}
        placeholder=""
        name="shipment.description"
        error={errors.shipment?.description}
        validation={{}}
      />
    </div>
  );
};

export default ShipmentSection;
